import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useUserRoles } from '@anytag/auth';
import { LinkRouterProps } from '@anytag/router';
import { SpreadButton } from '@anytag/shared-molecules';
import { cn, formatIntNumber } from '@anytag/shared-utils';
import { TableCell } from '@anytag/shared-atoms';
import { useTranslateCountry } from '@anytag/shared-hooks';
import { shieldInvalidImage, shieldValidImage } from '@anytag/shared-assets';
import { NetSuiteVerifiedStatus } from '@anytag/graphql';
import { ANALYTICS_SUBSCRIPTION_TRANSLATION_KEYS } from '@anytag/shared-constants';
import { DeleteAccountType, AccountType } from '../types';
import { useAccountsFilterOptions } from '../../AccountsFilter/hooks';
import { AccountListDomain } from '../../types';

export interface AccountsListTableProps {
  editRoute?: LinkRouterProps['to'];
  firstColumnTitle: string;
  onDeleting?: (account: DeleteAccountType | null) => void;
  domain: AccountListDomain;
}

export const useAccountsListTable = ({ editRoute, onDeleting, firstColumnTitle, domain }: AccountsListTableProps) => {
  const { tc } = useTranslateCountry();
  const { t } = useTranslation();
  const { isAdmin, isAdminStaff } = useUserRoles();
  const isAdvertiserPage = domain === 'advertiser';
  const { verifiedOptions, tikTokAdvertiserPlanOptions } = useAccountsFilterOptions();

  const getVerificationStatusProps = (status?: NetSuiteVerifiedStatus | null) => {
    const text = verifiedOptions.find((option) => option.value === status)?.label;

    switch (status) {
      case NetSuiteVerifiedStatus.VERIFIED:
        return { icon: shieldValidImage, text, color: cn('text-attention-green') };
      case NetSuiteVerifiedStatus.NOT_VERIFIED:
        return { icon: shieldInvalidImage, text, color: cn('text-attention-red') };
      case NetSuiteVerifiedStatus.NO_STATUS:
      default:
        return { icon: '', text, color: cn('text-black-70') };
    }
  };

  const columns: ColumnDef<AccountType>[] = [
    {
      accessorKey: 'name',
      header: firstColumnTitle || t<string>('HeaderColumn.Company Name'),
      cell: ({ row }) => (
        <div className="account-name-column">
          <TableCell.Link
            to={editRoute}
            disabled={!editRoute}
            label={row.original.name}
            params={{ id: row.original.id.toString() }}
          />
          <SpreadButton
            collapseFrom={0}
            moreButtonProps={{ disabled: !editRoute && !onDeleting }}
            options={[
              {
                label: t('Edit'),
                icon: 'edit',
                to: editRoute,
                hidden: !editRoute,
                tooltip: { help: t('Button.Edit') },
                params: { id: row.original.id.toString() }
              },
              {
                icon: 'delete',
                label: t('Delete'),
                disabled: !isAdmin,
                hidden: !onDeleting,
                tooltip: { help: t('Button.Delete') },
                onClick: () => onDeleting?.({ id: row.original.id, name: row.original.name })
              }
            ]}
          />
        </div>
      )
    },
    {
      accessorKey: 'tiktokPlan',
      meta: { hidden: !isAdvertiserPage },
      header: t<string>('HeaderColumn.TikTok Special Plan'),
      cell: ({ row }) => (
        <TableCell.Text
          className="min-w-25"
          value={
            (!!row.original.tiktokPlan &&
              tikTokAdvertiserPlanOptions.find((option) => option.value === row.original.tiktokPlan)?.label) ||
            '-'
          }
        />
      )
    },
    {
      accessorKey: 'analyticsPlan',
      meta: { hidden: !isAdvertiserPage },
      header: t<string>('HeaderColumn.Analytics Plan'),
      cell: ({ row }) => (
        <TableCell.Text
          className="min-w-25"
          value={
            (!!row.original.analyticsPlan && t(ANALYTICS_SUBSCRIPTION_TRANSLATION_KEYS[row.original.analyticsPlan])) ||
            '-'
          }
        />
      )
    },
    {
      accessorKey: 'verifiedStatus',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Verification Status'),
      cell: ({ row }) => {
        const { icon, text, color } = getVerificationStatusProps(row.original.verifiedStatus);

        return row.original.verifiedStatus ? (
          <div className={cn('flex items-center', color)}>
            <img alt="" src={icon} className="mr-1" />
            {text}
          </div>
        ) : (
          <div className="flex w-25 justify-center">-</div>
        );
      }
    },
    {
      accessorKey: 'phoneNumber',
      header: t<string>('HeaderColumn.Phone Number'),
      cell: ({ row }) => <TableCell.Text className="min-w-25" value={row.original.phoneNumber || '-'} />
    },
    {
      accessorKey: 'users',
      header: t<string>('HeaderColumn.Users'),
      cell: ({ row }) => <TableCell.Number className="min-w-25" value={formatIntNumber(row.original.users)} />,
      meta: { align: 'right' }
    },
    {
      accessorKey: 'country',
      header: t<string>('HeaderColumn.Country'),
      cell: ({ row }) => (
        <TableCell.Text
          className="min-w-25"
          value={row.original.country?.name ? tc(row.original.country?.name) : '-'}
        />
      )
    }
  ];

  return { columns };
};
